// CodeContainer.jsx
import React, { useContext, useCallback, useMemo } from 'react';
import CodeDisplay from './CodeDisplay'
import CodeableContext from 'contexts/CodeableContext';
import CodeOperationsContext from 'contexts/CodeOperationsContext';
import { getSelectedCodeIds } from 'utils/CodeUtils';
import { useExcerpt } from 'hooks/excerpts/queries';
import ProjectContext from 'contexts/ProjectContext';
import { useExpandParents } from 'hooks/codes/mutations/useExpandParents';
import useTrackingPage from 'hooks/useTrackingPage';


function CodeContainer({
  disabled,
  hideCodeCounts,
  showEmptyStateOnboarding
}) {
  const projectContext = useContext(ProjectContext);
  const { project } = projectContext;
  const projectID = project?.id;

  const {
    selectedExcerptId,
    select,
    codingUserId
  } = useContext(CodeableContext);
  
  const {
    onNewCode,
    addCodeToExcerpt: onAddCode,
    onRemoveCode
  } = useContext(CodeOperationsContext);

  const page = useTrackingPage();
  // Sidebar interactions on the code list — applying/removing/creating
  // a code from the right-hand pane.
  const sidebarTrackingContext = useMemo(
    () => ({ page, surface: 'code_list_sidebar' }),
    [page]
  );

  // Get the excerpt data from React Query cache
  const { data: excerpt } = useExcerpt(selectedExcerptId);

  // Calculate selected code IDs from the excerpt data
  const selectedCodeIds = useMemo(() =>
    getSelectedCodeIds(excerpt, codingUserId),
    [excerpt, codingUserId]
  );

  // Use the expandParents mutation
  const expandParentsMutation = useExpandParents(projectID);

  const onClick = useCallback((code_id, selected) => {
    if (!disabled) {
      if (!selected)
        onAddCode(code_id, null, sidebarTrackingContext);
      else
        onRemoveCode(code_id, null, sidebarTrackingContext);
    }

    // Expand parent codes when a code is clicked
    expandParentsMutation.mutate(code_id);
  }, [disabled, onAddCode, onRemoveCode, expandParentsMutation, sidebarTrackingContext]);

  // Wrap onNewCode so the sidebar code-input flow tags this as
  // code_list_sidebar surface without callers further downstream
  // having to know about tracking.
  const wrappedOnNewCode = useCallback(
    (codename, specificExcerptId = null) =>
      onNewCode(codename, specificExcerptId, sidebarTrackingContext),
    [onNewCode, sidebarTrackingContext]
  );

  return (
    <CodeDisplay
      disabled={disabled}
      selectedCodes={disabled ? [] : selectedCodeIds}
      onClick={onClick}
      addNewCode={wrappedOnNewCode}
      projectID={projectID}
      select={select}
      hideCodeCounts={hideCodeCounts}
      showEmptyStateOnboarding={showEmptyStateOnboarding}
    />
  );
}

export default CodeContainer;